/** Componentes de interface reutilizaveis, montados com `h()`. */

import { h, clear, type Child, type ElementProps } from './dom.js';
import type { ReadinessStatus, Role, EditionStatus } from '../types.js';
import { READINESS_LABELS, EDITION_STATUS_LABELS, ROLE_LABELS } from '../types.js';

/* ---------- Botoes -------------------------------------------------------- */

export interface ButtonOptions extends ElementProps {
  variant?: 'primary' | 'ghost' | 'subtle' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  block?: boolean;
  icon?: string;
  loading?: boolean;
  disabled?: boolean;
  onClick?: (event: MouseEvent) => void;
}

export function button(label: Child, options: ButtonOptions = {}): HTMLButtonElement {
  const { variant = 'primary', size = 'md', block, icon, loading, onClick, ...rest } = options;

  const classes = ['btn'];
  if (variant !== 'primary') classes.push(`btn--${variant}`);
  if (size !== 'md') classes.push(`btn--${size}`);
  if (block) classes.push('btn--block');

  const element = h(
    'button',
    {
      ...rest,
      class: classes.join(' '),
      attrs: { type: rest.attrs?.type ?? 'button' },
      ...(onClick ? { onclick: onClick } : {}),
      ...(loading || options.disabled ? { disabled: 'true' } : {}),
    },
    icon ? h('span', { text: icon, 'aria-hidden': 'true' }) : null,
    loading ? 'Aguarde…' : null,
    !loading ? label : null,
  );

  return element as HTMLButtonElement;
}

/* ---------- Cards --------------------------------------------------------- */

export interface CardOptions {
  title?: string;
  hint?: string;
  actions?: Child[];
  body?: Child | Child[];
  footer?: Child[];
  padded?: boolean;
  class?: string;
}

export function card(options: CardOptions): HTMLElement {
  const children: Child[] = [];

  if (options.title || options.actions) {
    children.push(
      h(
        'div',
        { class: 'card__header' },
        h(
          'div',
          null,
          h('div', { class: 'card__title', text: options.title ?? '' }),
          options.hint ? h('div', { class: 'card__hint', text: options.hint }) : null,
        ),
        options.actions ? h('div', { class: 'row' }, ...options.actions) : null,
      ),
    );
  }

  const bodyChildren = Array.isArray(options.body) ? options.body : [options.body];
  children.push(
    h(
      'div',
      { class: options.padded === false ? '' : 'card__body' },
      ...bodyChildren.filter(Boolean) as Child[],
    ),
  );

  if (options.footer && options.footer.length > 0) {
    children.push(h('div', { class: 'card__footer' }, ...options.footer));
  }

  return h('section', { class: `card${options.class ? ` ${options.class}` : ''}` }, ...children);
}

/* ---------- Badges -------------------------------------------------------- */

export function badge(
  text: string,
  variant: 'neutral' | 'accent' | 'info' | 'success' | 'warning' | 'danger' = 'neutral',
  withDot = false,
): HTMLElement {
  return h(
    'span',
    { class: `badge badge--${variant}` },
    withDot ? h('span', { class: 'dot' }) : null,
    text,
  );
}

const READINESS_VARIANT: Record<ReadinessStatus, 'neutral' | 'warning' | 'success'> = {
  PENDENTE: 'neutral',
  EM_PREPARACAO: 'warning',
  HOMOLOGADO: 'success',
};

export function readinessBadge(status: ReadinessStatus): HTMLElement {
  return badge(READINESS_LABELS[status], READINESS_VARIANT[status], true);
}

const EDITION_VARIANT: Record<EditionStatus, 'neutral' | 'success' | 'info' | 'danger'> = {
  PLANEJADA: 'info',
  ATIVA: 'success',
  ENCERRADA: 'neutral',
  CANCELADA: 'danger',
};

export function editionStatusBadge(status: EditionStatus): HTMLElement {
  return badge(EDITION_STATUS_LABELS[status], EDITION_VARIANT[status]);
}

export function roleBadge(role: Role): HTMLElement {
  const variant = role === 'ADMIN_GERAL' || role === 'PASTOR' ? 'accent' : 'neutral';
  return badge(ROLE_LABELS[role], variant);
}

/* ---------- Metricas ------------------------------------------------------ */

export function metric(
  label: string,
  value: Child,
  tone: 'default' | 'accent' | 'success' | 'warning' | 'info' = 'default',
): HTMLElement {
  return h(
    'div',
    { class: `metric${tone === 'default' ? '' : ` metric--${tone}`}` },
    h('div', { class: 'metric__value' }, value),
    h('div', { class: 'metric__label', text: label }),
  );
}

export function progressBar(percent: number): HTMLElement {
  return h(
    'div',
    { class: 'progress', attrs: { role: 'progressbar', 'aria-valuenow': Math.round(percent) } },
    h('div', { class: 'progress__bar', style: { width: `${Math.min(100, Math.max(0, percent))}%` } }),
  );
}

/* ---------- Formularios --------------------------------------------------- */

export interface FieldOptions {
  label: string;
  hint?: string;
  control: HTMLElement;
  required?: boolean;
}

export function field(options: FieldOptions): HTMLElement {
  return h(
    'label',
    { class: 'field' },
    h(
      'span',
      { class: 'field__label' },
      options.label,
      options.required ? h('span', { text: ' *', style: { color: 'var(--danger)' } }) : null,
    ),
    options.control,
    options.hint ? h('span', { class: 'field__hint', text: options.hint }) : null,
  );
}

export function input(props: ElementProps & { type?: string } = {}): HTMLInputElement {
  return h('input', { class: 'input', ...props }) as HTMLInputElement;
}

export function textarea(props: ElementProps = {}): HTMLTextAreaElement {
  return h('textarea', { class: 'textarea', ...props }) as HTMLTextAreaElement;
}

export function select(
  options: Array<{ value: string; label: string }>,
  props: ElementProps & { value?: string } = {},
): HTMLSelectElement {
  const { value, ...rest } = props;
  const element = h('select', { class: 'select', ...rest }) as HTMLSelectElement;

  for (const option of options) {
    element.append(h('option', { value: option.value, text: option.label }));
  }

  // O valor so pode ser atribuido depois que as <option> existem.
  if (value !== undefined) element.value = value;

  return element;
}

/* ---------- Tabelas ------------------------------------------------------- */

export function table(headers: string[], rows: Child[][], emptyMessage = 'Nenhum registro.'): HTMLElement {
  if (rows.length === 0) {
    return emptyState(emptyMessage);
  }

  return h(
    'div',
    { class: 'table-wrap' },
    h(
      'table',
      { class: 'table' },
      h('thead', null, h('tr', null, ...headers.map((label) => h('th', { text: label })))),
      h(
        'tbody',
        null,
        ...rows.map((cells) => h('tr', null, ...cells.map((cell) => h('td', null, cell)))),
      ),
    ),
  );
}

export function emptyState(message: string, icon = '○'): HTMLElement {
  return h(
    'div',
    { class: 'empty-state' },
    h('div', { class: 'empty-state__icon', text: icon }),
    h('div', { text: message }),
  );
}

export function loadingState(message = 'Carregando…'): HTMLElement {
  return h('div', { class: 'loading-screen' }, h('div', { text: message }));
}

/* ---------- Modal --------------------------------------------------------- */

export interface ModalOptions {
  title: string;
  body: Child | Child[];
  footer?: Child[];
  wide?: boolean;
  onClose?: () => void;
}

export interface ModalHandle {
  close: () => void;
  element: HTMLElement;
}

export function openModal(options: ModalOptions): ModalHandle {
  const root = document.getElementById('modal-root');
  if (!root) throw new Error('modal-root ausente');

  const close = (): void => {
    backdrop.remove();
    document.removeEventListener('keydown', onKeydown);
    options.onClose?.();
  };

  const onKeydown = (event: KeyboardEvent): void => {
    // Apenas o modal do topo responde ao Esc, permitindo composição.
    if (event.key !== 'Escape') return;
    if (root.lastElementChild === backdrop) close();
  };

  const bodyChildren = Array.isArray(options.body) ? options.body : [options.body];

  const backdrop = h(
    'div',
    {
      class: 'modal-backdrop',
      onclick: (event: MouseEvent) => {
        if (event.target === backdrop) close();
      },
    },
    h(
      'div',
      { class: `modal${options.wide ? ' modal--wide' : ''}`, attrs: { role: 'dialog', 'aria-modal': 'true' } },
      h(
        'div',
        { class: 'modal__head' },
        h('div', { class: 'modal__title', text: options.title }),
        button('✕', { variant: 'ghost', size: 'sm', onClick: close, 'aria-label': 'Fechar' }),
      ),
      h('div', { class: 'modal__body' }, ...(bodyChildren.filter(Boolean) as Child[])),
      options.footer ? h('div', { class: 'modal__foot' }, ...options.footer) : null,
    ),
  );

  // Modais empilham: fechar o do topo devolve o foco visual ao anterior.
  root.append(backdrop);
  document.addEventListener('keydown', onKeydown);

  return { close, element: backdrop };
}

/** Dialogo de confirmacao que resolve `true` ao confirmar. */
export function confirmDialog(options: {
  title: string;
  message: string;
  confirmLabel?: string;
  danger?: boolean;
  onConfirm: () => void | Promise<void>;
}): void {
  const confirmButton = button(options.confirmLabel ?? 'Confirmar', {
    variant: options.danger ? 'danger' : 'primary',
    onClick: () => {
      modal.close();
      void options.onConfirm();
    },
  });

  const modal = openModal({
    title: options.title,
    body: h('p', { text: options.message }),
    footer: [
      button('Cancelar', { variant: 'ghost', onClick: () => modal.close() }),
      confirmButton,
    ],
  });
}

/* ---------- Toasts -------------------------------------------------------- */

type ToastKind = 'success' | 'error' | 'info';

function pushToast(kind: ToastKind, title: string, message?: string): void {
  const root = document.getElementById('toasts');
  if (!root) return;

  const element = h(
    'div',
    { class: `toast toast--${kind}`, attrs: { role: 'status' } },
    h(
      'div',
      { class: 'toast__body' },
      h('div', { class: 'toast__title', text: title }),
      message ? h('div', { text: message }) : null,
    ),
    h('button', {
      class: 'btn btn--ghost btn--sm',
      text: '✕',
      attrs: { 'aria-label': 'Fechar aviso' },
      onclick: () => element.remove(),
    }),
  );

  root.append(element);
  setTimeout(() => element.remove(), kind === 'error' ? 9000 : 4500);
}

export const toast = {
  success: (title: string, message?: string) => pushToast('success', title, message),
  error: (title: string, message?: string) => pushToast('error', title, message),
  info: (title: string, message?: string) => pushToast('info', title, message),
};

/* ---------- Cabecalho de secao -------------------------------------------- */

export function sectionHeader(title: string, subtitle?: string, actions: Child[] = []): HTMLElement {
  return h(
    'div',
    { class: 'row row--between' },
    h(
      'div',
      { class: 'stack stack--sm' },
      h('h2', { text: title, style: { fontSize: '17px', fontWeight: '700' } }),
      subtitle ? h('div', { class: 'muted small', text: subtitle }) : null,
    ),
    actions.length > 0 ? h('div', { class: 'row' }, ...actions) : null,
  );
}

export function mediaChip(icon: string, label: string): HTMLElement {
  return h('span', { class: 'media-chip' }, h('span', { text: icon }), label);
}

/* ---------- Rodape de autoria --------------------------------------------- */

export const AUTHORS = {
  developer: 'Arnaldo Martins Hidalgo Junior',
  organization: 'DevMenthors Unimar',
} as const;

/**
 * Rodape institucional com a autoria do sistema.
 * Usado tanto no shell da aplicacao quanto na tela de login.
 */
export function appFooter(options: { variant?: 'default' | 'auth' } = {}): HTMLElement {
  const variant = options.variant ?? 'default';

  return h(
    'footer',
    { class: `app-footer${variant === 'auth' ? ' app-footer--auth' : ''}` },
    h(
      'div',
      { class: 'app-footer__credit' },
      h(
        'div',
        { class: 'app-footer__authors' },
        'Desenvolvido por ',
        h('strong', { text: AUTHORS.developer }),
        h('span', { text: ' · ' }),
        h('strong', { text: AUTHORS.organization }),
      ),
      h('div', {
        text: 'Sistema de Ministrações Face a Face · V2',
      }),
    ),
    h(
      'div',
      { class: 'app-footer__mark' },
      h('span', { class: 'app-footer__mark-badge', text: 'DM' }),
      h('span', { text: AUTHORS.organization }),
    ),
  );
}

